import { IDispatchId } from '../../utils/helpers/redux.helper';
import { IEntityTypePayload } from '../entity-data/entity-data.actions';
import { EntityTypesEnum } from '../../app.enum';
import { List } from 'immutable';
import { Document } from './document.model';
import { NavigationExtras, Params } from '@angular/router';
import { DocumentItem } from './sub-models/document-item.model';
import { IEditTaxRateProperties } from './document-data.data';
import { IMessageDeliveryData } from '../message-delivery/message-delivery.interface';

// common interfaces
export interface IDocumentPayload {
  document: Document;
}

export interface IDocumentIdPayload {
  documentId: string;
}

export interface ISetEditingDocument {
  setEditingDocument: boolean;
}

// action payload interfaces
export interface IDocumentActionPayload
  extends IDispatchId,
    IEntityTypePayload,
    IDocumentPayload,
    ISetEditingDocument {}

export interface IPrintDocument extends IDocumentActionPayload {}

export interface IMarkAsPrinted extends IDocumentActionPayload {}

export interface ICopyDocument extends IDispatchId, IEntityTypePayload {
  sourceDocument: Document;
  copyClient: boolean;
  copyItems: boolean;
  copyAttachments: boolean;
  copyTaxes: boolean;
  saveCopy: boolean;
  saveEditing: boolean;
  queryParams?: Params;
}

export interface ISaveCopiedDocument extends IDispatchId, IEntityTypePayload {
  sourceDocument: Document;
  copiedDocument: Document;
  saveEditing: boolean;
  queryParams: Params;
}

export interface IRedirectToCopiedDocument extends ISaveCopiedDocument {
  isSavedCopy: boolean;
  queryParams: Params;
}

export interface IGoToDocumentAddClientScreen
  extends IDocumentIdPayload,
    IEntityTypePayload {
  navigationExtras: NavigationExtras;
}

export interface IDocumentItemPayload extends IEntityTypePayload {
  documentItem: DocumentItem;
}

export interface IDocumentItemsPayload extends IEntityTypePayload {
  documentItems: List<DocumentItem>;
}

export interface IUpdateTaxInEditing extends IEntityTypePayload {
  editTaxRateProperties: IEditTaxRateProperties;
}

export interface IMoveDocumentItemPayload extends IEntityTypePayload {
  oldIndex: number;
  newIndex: number;
  isPart: boolean;
}

export interface IDeleteFromTmpCache extends IEntityTypePayload {
  key: string;
}

export interface ISetInTmpCache extends IDeleteFromTmpCache {
  value: any;
}

export interface IEnableCardPayments
  extends IDispatchId,
    IEntityTypePayload,
    IDocumentIdPayload,
    ISetEditingDocument {
  href: string;
}

export interface ISendDocument
  extends IDispatchId,
    IEntityTypePayload,
    IDocumentPayload {
  bankTransferFeatureEnabled: boolean;
  cardPaymentsFeatureEnabled: boolean;
  paypalFeatureEnabled: boolean;
  messageDeliveryData: IMessageDeliveryData;
}

// action enums
export enum ActionsEnum {
  PRINT_DOCUMENT = 'PRINT_DOCUMENT',
  MARK_AS_PRINTED = 'MARK_AS_PRINTED',
  COPY_DOCUMENT = 'COPY_DOCUMENT',
  SAVE_COPIED_DOCUMENT = 'SAVE_COPIED_DOCUMENT',
  REDIRECT_TO_COPIED_DOCUMENT = 'REDIRECT_TO_COPIED_DOCUMENT',
  GO_TO_DOCUMENT_ADD_CLIENT_SCREEN = 'GO_TO_DOCUMENT_ADD_CLIENT_SCREEN',
  ADD_DOCUMENT_ITEM_TO_EDITING = 'ADD_DOCUMENT_ITEM_TO_EDITING',
  UPDATE_DOCUMENT_ITEM_IN_EDITING = 'UPDATE_DOCUMENT_ITEM_IN_EDITING',
  DELETE_DOCUMENT_ITEM_FROM_EDITING = 'DELETE_DOCUMENT_ITEM_FROM_EDITING',
  MOVE_DOCUMENT_ITEM_IN_EDITING = 'MOVE_DOCUMENT_ITEM_IN_EDITING',
  BATCH_ADD_DOCUMENT_ITEMS_TO_EDITING = 'BATCH_ADD_DOCUMENT_ITEMS_TO_EDITING',
  UPDATE_TAX_IN_EDITING = 'UPDATE_TAX_IN_EDITING',
  SET_IN_TMP_CACHE = 'SET_IN_TMP_CACHE',
  DELETE_FROM_TMP_CACHE = 'DELETE_FROM_TMP_CACHE',
  ENABLE_CARD_PAYMENTS = 'ENABLE_CARD_PAYMENTS',
  SEND_DOCUMENT = 'SEND_DOCUMENT',
}

// action definitions
export interface PrintDocument {
  type: ActionsEnum.PRINT_DOCUMENT;
  payload: IPrintDocument;
}

export interface MarkAsPrinted {
  type: ActionsEnum.MARK_AS_PRINTED;
  payload: IMarkAsPrinted;
}

export interface CopyDocument {
  type: ActionsEnum.COPY_DOCUMENT;
  payload: ICopyDocument;
}

export interface SaveCopiedDocument {
  type: ActionsEnum.SAVE_COPIED_DOCUMENT;
  payload: ISaveCopiedDocument;
}

export interface RedirectToCopiedDocument {
  type: ActionsEnum.REDIRECT_TO_COPIED_DOCUMENT;
  payload: IRedirectToCopiedDocument;
}

export interface GoToDocumentAddClientScreen {
  type: ActionsEnum.GO_TO_DOCUMENT_ADD_CLIENT_SCREEN;
  payload: IGoToDocumentAddClientScreen;
}

export interface AddDocumentItemToEditing {
  type: ActionsEnum.ADD_DOCUMENT_ITEM_TO_EDITING;
  payload: IDocumentItemPayload;
}

export interface UpdateDocumentItemInEditing {
  type: ActionsEnum.UPDATE_DOCUMENT_ITEM_IN_EDITING;
  payload: IDocumentItemPayload;
}

export interface DeleteDocumentItemFromEditing {
  type: ActionsEnum.DELETE_DOCUMENT_ITEM_FROM_EDITING;
  payload: IDocumentItemPayload;
}

export interface MoveDocumentItemInEditing {
  type: ActionsEnum.MOVE_DOCUMENT_ITEM_IN_EDITING;
  payload: IMoveDocumentItemPayload;
}

export interface BatchAddDocumentItemsToEditing {
  type: ActionsEnum.BATCH_ADD_DOCUMENT_ITEMS_TO_EDITING;
  payload: IDocumentItemsPayload;
}

export interface UpdateTaxInEditing {
  type: ActionsEnum.UPDATE_TAX_IN_EDITING;
  payload: IUpdateTaxInEditing;
}

export interface SetInTmpCache {
  type: ActionsEnum.SET_IN_TMP_CACHE;
  payload: ISetInTmpCache;
}

export interface DeleteFromTmpCache {
  type: ActionsEnum.DELETE_FROM_TMP_CACHE;
  payload: IDeleteFromTmpCache;
}

export interface EnableCardPayments {
  type: ActionsEnum.ENABLE_CARD_PAYMENTS;
  payload: IEnableCardPayments;
}

export interface SendDocument {
  type: ActionsEnum.SEND_DOCUMENT;
  payload: ISendDocument;
}

// action creators
export const printDocument = (
  dispatchId: string,
  entityType: EntityTypesEnum,
  document: Document,
  setEditingDocument: boolean
): PrintDocument => ({
  type: ActionsEnum.PRINT_DOCUMENT,
  payload: {
    dispatchId,
    entityType,
    document,
    setEditingDocument,
  },
});

export const markAsPrinted = (
  dispatchId: string,
  entityType: EntityTypesEnum,
  document: Document,
  setEditingDocument: boolean
): MarkAsPrinted => ({
  type: ActionsEnum.MARK_AS_PRINTED,
  payload: {
    dispatchId,
    entityType,
    document,
    setEditingDocument,
  },
});

export const copyDocument = (payload: ICopyDocument): CopyDocument => ({
  type: ActionsEnum.COPY_DOCUMENT,
  payload,
});

export const saveCopiedDocument = (
  dispatchId: string,
  entityType: EntityTypesEnum,
  sourceDocument: Document,
  copiedDocument: Document,
  saveEditing: boolean,
  queryParams: Params = null
): SaveCopiedDocument => ({
  type: ActionsEnum.SAVE_COPIED_DOCUMENT,
  payload: {
    dispatchId,
    entityType,
    sourceDocument,
    copiedDocument,
    saveEditing,
    queryParams,
  },
});

export const redirectToCopiedDocument = (
  dispatchId: string,
  entityType: EntityTypesEnum,
  sourceDocument: Document,
  copiedDocument: Document,
  isSavedCopy: boolean,
  saveEditing: boolean,
  queryParams: Params = null
): RedirectToCopiedDocument => ({
  type: ActionsEnum.REDIRECT_TO_COPIED_DOCUMENT,
  payload: {
    dispatchId,
    entityType,
    sourceDocument,
    copiedDocument,
    isSavedCopy,
    saveEditing,
    queryParams,
  },
});

export const goToDocumentAddClientScreen = (
  entityType: EntityTypesEnum,
  documentId: string,
  navigationExtras?: NavigationExtras
): GoToDocumentAddClientScreen => ({
  type: ActionsEnum.GO_TO_DOCUMENT_ADD_CLIENT_SCREEN,
  payload: {
    entityType,
    documentId,
    navigationExtras,
  },
});

export const addDocumentItemToEditing = (
  entityType: EntityTypesEnum,
  documentItem: DocumentItem
): AddDocumentItemToEditing => ({
  type: ActionsEnum.ADD_DOCUMENT_ITEM_TO_EDITING,
  payload: {
    entityType,
    documentItem,
  },
});

export const updateDocumentItemInEditing = (
  entityType: EntityTypesEnum,
  documentItem: DocumentItem
): UpdateDocumentItemInEditing => ({
  type: ActionsEnum.UPDATE_DOCUMENT_ITEM_IN_EDITING,
  payload: {
    entityType,
    documentItem,
  },
});

export const deleteDocumentItemFromEditing = (
  entityType: EntityTypesEnum,
  documentItem: DocumentItem
): DeleteDocumentItemFromEditing => ({
  type: ActionsEnum.DELETE_DOCUMENT_ITEM_FROM_EDITING,
  payload: {
    entityType,
    documentItem,
  },
});

export const moveDocumentItemInEditing = (
  entityType: EntityTypesEnum,
  oldIndex: number,
  newIndex: number,
  isPart: boolean
): MoveDocumentItemInEditing => ({
  type: ActionsEnum.MOVE_DOCUMENT_ITEM_IN_EDITING,
  payload: {
    entityType,
    oldIndex,
    newIndex,
    isPart,
  },
});

export const batchAddDocumentItemsToEditing = (
  entityType: EntityTypesEnum,
  documentItems: List<DocumentItem>
): BatchAddDocumentItemsToEditing => ({
  type: ActionsEnum.BATCH_ADD_DOCUMENT_ITEMS_TO_EDITING,
  payload: {
    entityType,
    documentItems,
  },
});

export const updateTaxInEditing = (
  entityType: EntityTypesEnum,
  editTaxRateProperties: IEditTaxRateProperties
): UpdateTaxInEditing => ({
  type: ActionsEnum.UPDATE_TAX_IN_EDITING,
  payload: {
    entityType,
    editTaxRateProperties,
  },
});

export const setInTmpCache = (
  entityType: EntityTypesEnum,
  key: string,
  value: any
): SetInTmpCache => ({
  type: ActionsEnum.SET_IN_TMP_CACHE,
  payload: {
    entityType,
    key,
    value,
  },
});

export const deleteFromTmpCache = (
  entityType: EntityTypesEnum,
  key: string
): DeleteFromTmpCache => ({
  type: ActionsEnum.DELETE_FROM_TMP_CACHE,
  payload: {
    entityType,
    key,
  },
});

export const enableCardPayments = (
  dispatchId: string,
  entityType: EntityTypesEnum,
  documentId: string,
  href: string,
  setEditingDocument: boolean
): EnableCardPayments => ({
  type: ActionsEnum.ENABLE_CARD_PAYMENTS,
  payload: {
    dispatchId,
    entityType,
    documentId,
    href,
    setEditingDocument,
  },
});

export const sendDocument = (
  dispatchId: string,
  entityType: EntityTypesEnum,
  document: Document,
  bankTransferFeatureEnabled: boolean,
  cardPaymentsFeatureEnabled: boolean,
  paypalFeatureEnabled: boolean,
  messageDeliveryData: IMessageDeliveryData
): SendDocument => ({
  type: ActionsEnum.SEND_DOCUMENT,
  payload: {
    dispatchId,
    entityType,
    document,
    bankTransferFeatureEnabled,
    cardPaymentsFeatureEnabled,
    paypalFeatureEnabled,
    messageDeliveryData,
  },
});
